import debounce from 'lodash.debounce'
import React from 'react'

import { useI18n } from '../../../configuration/contexts/I18nContext'
import styled, { linkStyles } from '../../constants/theme'
import labelWithRequiredIndicator from '../../helpers/labelWithRequiredIndicator'
import { FormContext, isValidWhenNullOrFalse } from '../../hooks/useForm'
import { capitalizeFirstLetter } from '../../lib/string'
import { SelectorIcon } from '../elements/Icons'
import PillButton from '../elements/PillButton'
import FormElementFactory from './FormElementFactory'
import {
  BasicType,
  ElementState,
  ElementType,
  FormElementProps,
  FormElementState,
  Label,
  QuestionType,
  RightButtonContainer,
  RightIconContainer,
} from './FormElementParts'

interface Cancelable {
  cancel(): void
}

interface EditButtonProps {
  label?: string
  onEdit?: () => void
}

interface ElementWrapperProps {
  display?: string
}

const DENYLISTED_LABEL_TYPES: (BasicType | ElementType | QuestionType)[] = [
  ElementType.AUTOCOMPLETE,
  ElementType.CHECKBOX,
  ElementType.DATE,
  ElementType.RADIO,
  ElementType.SWITCH,
]

const CharsRemainingWrapper = styled.div(
  {
    fontSize: 13,
    fontWeight: 'bold',
    textAlign: 'right',
  },
  ({ theme }) => ({
    '&.invalid': {
      color: theme.error,
    },
  }),
)
const CharsRemaining: React.FC<{
  charsRemaining: number
  className: string
}> = ({ charsRemaining, className }) => {
  const i18n = useI18n()

  return (
    <CharsRemainingWrapper className={className}>
      {i18n.t('formElementHelpers.charsRemaining', { num: charsRemaining })}
    </CharsRemainingWrapper>
  )
}

const EditButton: React.FC<EditButtonProps> = (props) => {
  const i18n = useI18n()
  const { label, onEdit } = props

  return (
    <PillButton ariaLabel={`Edit ${label}`} onClick={onEdit}>
      {i18n.t('edit')}
    </PillButton>
  )
}

// TODO: Cut this over to a functional component?
class FormElement extends React.Component<FormElementProps, FormElementState> {
  static defaultProps: Partial<FormElementProps> = {
    description: '',
    elementState: ElementState.INDETERMINATE,
    errorText: '',
    includeBlank: undefined,
    options: [],
    requiresPassword: false,
    value: '',
  }

  static contextType = FormContext

  state = {
    elementState: this.props.elementState || ElementState.INDETERMINATE,
    serverErrors: this.props.serverErrors,
    value: this.props.value || '',
  }

  debouncedInvalid: (() => void) & Cancelable

  constructor(props) {
    super(props)
    this.debouncedInvalid = debounce(this.setInvalid, 1500)
  }

  componentDidMount() {
    const { isRequired, name, requiresPassword, value } = this.props
    if (this.onFormElementChange() && !requiresPassword) {
      this.validate()
      this.onFormElementChange()(
        name,
        value,
        isRequired,
        this.props.validations,
      )
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.elementState !== this.props.elementState) {
      this.setState({
        elementState: this.props.elementState || ElementState.INDETERMINATE,
      })
    }
    if (prevProps.serverErrors !== this.props.serverErrors) {
      this.setState({ serverErrors: this.props.serverErrors }, this.validate)
    }
    if (
      prevProps.value !== this.props.value &&
      prevProps.value === this.state.value
    ) {
      this.setState({ value: this.props.value }, () => {
        this.validate()
      })
    }

    if (prevProps.display !== this.props.display) {
      if (this.props.display === 'none') {
        this.setState({ value: null }, () => {
          if (this.onFormElementChange()) {
            this.onFormElementChange()(
              this.props.name,
              null,
              this.props.isRequired,
              this.props.validations,
            )
          }
          this.validate()
        })
      }
    }
  }

  validate(): boolean {
    let valid = true
    let clientValid = true
    let serverValid = true
    const { elementState, isRequired, validations } = this.props
    const { serverErrors, value } = this.state
    if (isValidWhenNullOrFalse(isRequired, value)) {
      clientValid = true
    } else if (
      elementState === ElementState.INDETERMINATE &&
      validations?.length
    ) {
      clientValid = validations.every((v) => v(value))
    }
    if (serverErrors?.[this.props.name || '']) {
      serverValid = false
    }
    valid = clientValid && serverValid
    if (valid) {
      this.debouncedInvalid.cancel()
      this.setState({ elementState: ElementState.VALID })
    } else {
      if (value !== this.props.value || !serverValid) {
        this.debouncedInvalid()
      } else {
        this.debouncedInvalid.cancel()
        this.setState({
          elementState: elementState || ElementState.INDETERMINATE,
        })
      }
    }
    return valid
  }

  setInvalid() {
    this.setState({ elementState: ElementState.INVALID })
  }

  handleChange = ({ name, value }: { name: string; value: string }) => {
    const newErrors = this.state.serverErrors
    const persistError = this.props.persistError
    if (!persistError && newErrors) {
      delete newErrors[name]
    }
    this.setState({ value, serverErrors: newErrors }, () => {
      const { isRequired, onChange } = this.props
      if (onChange) {
        onChange({ [name]: value })
      }
      if (this.onFormElementChange()) {
        this.validate()
        this.onFormElementChange()(
          name,
          value,
          isRequired,
          this.props.validations,
        )
      }
    })
  }

  onFormElementChange = () =>
    this.props.onFormElementChange || (this.context as any)?.onFormElementChange

  validClass(state: ElementState): string {
    return this.props.persistError ? 'info' : state.toLowerCase()
  }

  render() {
    const {
      description,
      disabled,
      display,
      errorText,
      id,
      isRequired,
      label,
      name,
      onEdit,
      requiresPassword,
      rightButton,
      rightIcon,
      showCharsRemaining,
      type,
    } = this.props
    const { elementState, serverErrors, value } = this.state
    const hasSeverError = serverErrors?.[name || '']
    const showClientsideError =
      !hasSeverError && elementState === ElementState.INVALID
    let unifiedErrorText = ''
    if (showClientsideError && errorText && errorText.length > 0) {
      unifiedErrorText = errorText
    } else if (serverErrors?.[name || '']) {
      unifiedErrorText = serverErrors[name || '']
    }
    const charsRemaining =
      (showCharsRemaining && showCharsRemaining - value.length) || 0
    const elementWrapperClassName =
      capitalizeFirstLetter(type) + `Element ${name}`
    const elementWrapperId = id || undefined

    const isLabelAllowed = !DENYLISTED_LABEL_TYPES.includes(type)

    return (
      <ElementWrapper
        className={elementWrapperClassName}
        id={elementWrapperId}
        display={display}
      >
        {isLabelAllowed && (
          <Label
            dangerouslySetInnerHTML={{
              __html: labelWithRequiredIndicator({ isRequired, label }) || '',
            }}
            htmlFor={name}
          />
        )}
        <InnerDiv>
          <InputContainer
            className={`${this.validClass(elementState)}`}
            onClick={requiresPassword ? onEdit : undefined}
            rightIcon={rightIcon}
            type={type}
          >
            <FormElementFactory
              {...this.props}
              elementState={elementState}
              disabled={disabled || requiresPassword}
              onChange={this.handleChange}
              unifiedErrorText={unifiedErrorText}
              value={value || ''}
            />
            {rightButton && (
              <RightButtonContainer>{rightButton}</RightButtonContainer>
            )}
            {rightIcon && <RightIconContainer>{rightIcon}</RightIconContainer>}
            {!rightIcon && type === ElementType.SELECT && (
              <RightIconContainer>
                <SelectorIcon />
              </RightIconContainer>
            )}
          </InputContainer>
          {requiresPassword && !disabled && (
            <EditButton label={label} onEdit={onEdit} />
          )}
        </InnerDiv>
        {!!(
          showCharsRemaining &&
          (type === ElementType.TEXT || type === ElementType.TEXTAREA)
        ) && (
          <CharsRemaining
            charsRemaining={charsRemaining}
            className={charsRemaining < 0 ? 'invalid' : ''}
          />
        )}
        {description && description.length > 0 && (
          <Description
            className="description"
            dangerouslySetInnerHTML={{ __html: description }}
          />
        )}
        {unifiedErrorText.length > 0 && (
          <ErrorDiv
            id={`${name}Error`}
            className={this.validClass(elementState)}
          >
            {Array.isArray(unifiedErrorText) ? (
              <ul>
                {unifiedErrorText.map((error, i) => (
                  <li key={i}>{error}</li>
                ))}
              </ul>
            ) : (
              unifiedErrorText
            )}
          </ErrorDiv>
        )}
      </ElementWrapper>
    )
  }
}

export const ServerErrorText = ({ name, serverErrors }) => {
  const error = serverErrors?.[name]
  if (!error) {
    return null
  }
  const formattedName = capitalizeFirstLetter(name.replace(/([A-Z])/g, ' $1'))
  const fullError = `${formattedName} ${error}`
  return <ErrorDiv id={`${name}Error`}>{fullError}</ErrorDiv>
}

const Description = styled.div({
  '& a': {
    ...linkStyles,
  },
  fontSize: 13,
  paddingBottom: 10,
})

const ElementWrapper = styled.div(
  {
    marginBottom: 5,
  },
  ({ display }: ElementWrapperProps) => (display === 'none' ? { display } : {}),
)

const InnerDiv = styled.div({
  '& [aria-label^="Edit"]': {
    marginLeft: 10,
  },
  alignItems: 'center',
  display: 'flex',
  position: 'relative',
})

const ErrorDiv = styled.div(
  {
    fontSize: 12,
    fontWeight: 'bold',
    marginBottom: 5,
    marginLeft: 1,
    minHeight: 20,
  },
  ({ theme }) => ({
    '&:not(.info)': {
      color: theme.error,
    },
  }),
)

export interface InputContainerProps {
  onClick?: any
  rightIcon?: JSX.Element
  type: BasicType | ElementType | QuestionType
}

const InputContainer = styled.div<InputContainerProps>(
  {
    '& input:focus:not([class^="switch"]) ~ span:first-of-type:before, & input:focus:not([class^="switch"]) ~ span:first-of-type:after':
      {
        width: '50%',
      },
    '& select:focus ~ span:first-of-type:before, & select:focus ~ span:first-of-type:after':
      {
        width: '50%',
      },
    '& textarea:focus ~ span:first-of-type:before, & textarea:focus ~ span:first-of-type:after':
      {
        width: '50%',
      },
    position: 'relative',
    width: '100%',
  },
  ({ rightIcon, type, theme }) => ({
    'input[type="checkbox"]:checked ~ div[class^="box"]': {
      background: theme.primaryDark,
    },
    '& > input, & > select, & > textarea': {
      paddingRight:
        rightIcon || type === ElementType.SELECT
          ? theme.padding
          : theme.paddingSmall,
    },
    transition: `border ${theme.speed} ${theme.ease}`,
  }),
)

export default React.forwardRef((props: FormElementProps, ref) => (
  <FormElement
    {...props}
    innerRef={typeof ref === 'function' ? ref : undefined}
  />
))
